import { type Article } from '~/types/article';
import { type InlineAd } from '~/types/articleInline';

/**
 * AutoInlineAdProcessor
 *
 * Handles automatic inline ad placement based on character-weight thresholds
 * and render logic configuration from articleAdConfig.inline[].renderLogic.
 *
 * This processor is used when article.meta.manualAdPlacement is NOT true.
 */
export default class AutoInlineAdProcessor {
  private readonly articleAdConfig: any;
  private readonly inlineAdWeight: any;
  private readonly considerElementChars: string[] = ['subhead', 'quote', 'text'];
  private readonly deviceType: string = getDeviceType();
  private readonly shouldPlaceInlineAdBeforeSubhead: boolean;

  constructor (articleAdConfig: any) {
    this.articleAdConfig = articleAdConfig;
    this.inlineAdWeight = articleAdConfig?.inlineComponentWeight;
    this.shouldPlaceInlineAdBeforeSubhead = articleAdConfig?.shouldPlaceInlineAdBeforeSubhead || false;
  }

  /*
   * ---------------------------------------------------------------------------
   * Public entry point
   * ---------------------------------------------------------------------------
   */

  process (article: Article): void {
    this.buildCharacterCountMap(article);
    this.placeInlineAds(article);
    this.cleanupTemporaryProperties(article);
  }

  /*
   * ---------------------------------------------------------------------------
   * Character-count map helpers
   * ---------------------------------------------------------------------------
   */

  /**
   * Build character count data and append directly to article body elements
   * as temporary properties (elementWeight, cumulativeCount).
   * These are removed by cleanupTemporaryProperties after placement.
   */
  private buildCharacterCountMap (article: Article): void {
    let cumulativeCount = 0;

    for (let i = 0; i < article.body.length; i++) {
      const element = article.body[i] as any;
      let elementWeight = 0;

      if (this.hasType(element) && this.considerElementChars.includes(element.type)) {
        const content = this.getElementContent(element);
        elementWeight = this.getCountedCharacter(content);
        cumulativeCount += elementWeight;
      } else if (this.hasType(element)) {
        elementWeight = this.inlineAdWeight?.[this.deviceType]?.[element.type] || 0;
        cumulativeCount += elementWeight;
      }

      (article.body[i] as any).elementWeight = elementWeight;
      (article.body[i] as any).cumulativeCount = cumulativeCount;
    }
  }

  /**
   * Get the nearest cumulativeCount at or before the given position.
   * Resilient to injected ad elements that do not have a cumulativeCount property.
   */
  private getNearestCumulativeCount (article: Article, position: number): number {
    for (let i = position; i >= 0; i--) {
      const count = (article.body[i] as any)?.cumulativeCount;
      if (typeof count === 'number') {
        return count;
      }
    }
    return 0;
  }

  /**
   * Remove temporary properties from article body elements after ad placement.
   */
  private cleanupTemporaryProperties (article: Article): void {
    for (const element of article.body) {
      if (Object.prototype.hasOwnProperty.call(element, 'elementWeight')) {
        delete (element as any).elementWeight;
      }
      if (Object.prototype.hasOwnProperty.call(element, 'cumulativeCount')) {
        delete (element as any).cumulativeCount;
      }
    }
  }

  /*
   * ---------------------------------------------------------------------------
   * Element type helpers
   * ---------------------------------------------------------------------------
   */

  private hasType (element: any): element is { type: string } {
    return 'type' in element && typeof element.type === 'string';
  }

  private isTextOrSubhead (element: any): boolean {
    return this.hasType(element) && (element.type === 'text' || element.type === 'subhead');
  }

  private getElementContent (element: any): string {
    if ('html' in element && element.html) { return element.html; }
    if ('value' in element && element.value) { return element.value; }
    if ('quote' in element && element.quote) { return element.quote; }
    return '';
  }

  private getCountedCharacter (characters: string): number {
    return characters?.replace(/<\/?[^>]+(>|$)/g, ' ').length || 0;
  }

  /*
   * ---------------------------------------------------------------------------
   * Position validation
   * ---------------------------------------------------------------------------
   */

  /**
   * Find the position of the first subhead within a minAfter character range
   * starting from startPosition. Returns -1 if none found.
   */
  private findSubheadWithinRange (article: Article, startPosition: number, lookUpRange: number, ad: any): number {
    let incrementalCount = 0;
    for (let i = startPosition + 1; i < article.body.length; i++) {
      const element = article.body[i] as any;

      if (this.hasType(element) && element.type === 'subhead') {
        const prevElementIndex = i - 1;
        if (this.checkCorrectPosition(article, prevElementIndex, ad)) {
          return i;
        }
      }

      if (!this.isTextOrSubhead(element)) {
        break;
      }

      incrementalCount += element?.elementWeight || 0;
      if (incrementalCount >= lookUpRange) {
        break;
      }
    }
    return -1;
  }

  /**
   * Determines if an ad can be placed at the specified position in the article body.
   * Ensures sufficient text characters exist before and after the position.
   */
  private checkCorrectPosition (article: Article, position: number, ad: any): boolean {
    const deviceRenderLogic = ad.renderLogic?.[this.deviceType] ?? {};
    const minBefore = deviceRenderLogic.minCharactersAbove ?? 0;
    const minAfter = deviceRenderLogic.minCharactersBelow ?? 0;

    if (minBefore === 0) {
      const currentElement = article.body[position] as any;
      if (!currentElement || !this.hasType(currentElement) || currentElement.type !== 'text') {
        return false;
      }
    }

    if (minAfter === 0) {
      const nextElement = article.body[position + 1] as any;
      if (this.shouldPlaceInlineAdBeforeSubhead) {
        if (!nextElement || !this.isTextOrSubhead(nextElement)) {
          return false;
        }
      } else if (!nextElement || !this.hasType(nextElement) || nextElement.type !== 'text') {
        return false;
      }
    }

    if (minBefore === 0 && minAfter === 0) {
      return true;
    }

    if (minBefore > 0) {
      let beforeCharCount = 0;
      for (let i = position; i >= 0; i--) {
        const element = article.body[i] as any;
        if (!element || !this.hasType(element) || element.type !== 'text') {
          break;
        }
        beforeCharCount += (article.body[i] as any)?.elementWeight || 0;
        if (beforeCharCount >= minBefore) {
          break;
        }
      }
      if (beforeCharCount < minBefore) {
        return false;
      }
    }

    if (minAfter > 0) {
      let afterCharCount = 0;
      for (let i = position + 1; i < article.body.length; i++) {
        const element = article.body[i] as any;
        const shouldBreak = this.shouldPlaceInlineAdBeforeSubhead
          ? !element || !this.isTextOrSubhead(element)
          : !element || !this.hasType(element) || element.type !== 'text';

        if (shouldBreak) {
          break;
        }

        afterCharCount += (article.body[i] as any)?.elementWeight || 0;
        if (afterCharCount >= minAfter) {
          break;
        }
      }
      if (afterCharCount < minAfter) {
        return false;
      }
    }

    return true;
  }

  /*
   * ---------------------------------------------------------------------------
   * Core placement logic
   * ---------------------------------------------------------------------------
   */

  /**
   * Iterates over articleAdConfig.inline and places each ad into article.body
   * once the cumulative character-weight threshold (minContentWeightAbove) is exceeded
   * and the position passes checkCorrectPosition.
   */
  private placeInlineAds (article: Article): void {
    if (!article.body?.length || !this.articleAdConfig?.inline) { return; }

    let adPosition = 0;

    this.articleAdConfig.inline.forEach((ad: any) => {
      let incrementalCharCount = 0;

      if (!shouldShowAd(ad) || !ad?.renderLogic) { return; }

      const charAfterStart =
        ad.renderLogic.minContentWeightAbove ??
        ad.renderLogic[this.deviceType]?.minContentWeightAbove;

      if (!charAfterStart || charAfterStart <= 0) { return; }

      const inlineAd: InlineAd = {
        type: 'ad',
        ad: {
          ...ad,
          injected: true
        }
      };

      for (let i = adPosition; i < article.body.length; i++) {
        incrementalCharCount += (article.body[i] as any)?.elementWeight || 0;

        if (incrementalCharCount > charAfterStart && this.checkCorrectPosition(article, i, ad)) {
          const subHeadLookUpRange = ad.renderLogic[this.deviceType]?.subheadSearchWeightThreshold || 0;

          if (this.shouldPlaceInlineAdBeforeSubhead && subHeadLookUpRange > 0) {
            const subheadPosition = this.findSubheadWithinRange(article, i, subHeadLookUpRange, ad);
            if (subheadPosition > 0) {
              adPosition = subheadPosition;
              (inlineAd as any).elementWeight = 0;
              (inlineAd as any).cumulativeCount = this.getNearestCumulativeCount(article, adPosition - 1);
              article.body.splice(adPosition, 0, inlineAd);
              break;
            }
          }

          adPosition = i + 1;
          (inlineAd as any).elementWeight = 0;
          (inlineAd as any).cumulativeCount = this.getNearestCumulativeCount(article, adPosition - 1);
          article.body.splice(adPosition, 0, inlineAd);
          break;
        }
      }
    });
  }
}
